var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from "react";
import { Loader } from "../loader/index.js";
import { useRenameCSS } from "../theme/Theme.js";
import { Label } from "../typography/index.js";
import { omit } from "../util/index.js";
import { composeEventHandlers } from "../util/composeEventHandlers.js";
/**
 * A button component
 * @see [📝 Documentation](https://aksel.nav.no/komponenter/core/button)
 * @see 🏷️ {@link ButtonProps}
 * @see [🤖 OverridableComponent](https://aksel.nav.no/grunnleggende/kode/overridablecomponent) support
 * @example
 * ```jsx
 * <Button>Klikk meg</Button>
 * ```
 */
export const Button = forwardRef((_a, ref) => {
    var { as: Component = "button", variant = "primary", className, children, size = "medium", loading = false, disabled, icon, iconPosition = "left", onKeyUp, "data-color": color } = _a, rest = __rest(_a, ["as", "variant", "className", "children", "size", "loading", "disabled", "icon", "iconPosition", "onKeyUp", "data-color"]);
    const { cn } = useRenameCSS();
    const filterProps = disabled || loading ? omit(rest, ["href"]) : rest;
    const handleKeyUp = (e) => {
        if (e.key === " " && !disabled && !loading) {
            e.currentTarget.click();
        }
    };
    return (React.createElement(Component, Object.assign({}, (Component !== "button" ? { role: "button" } : {}), { "data-color": color !== null && color !== void 0 ? color : variantToColor(variant), "data-variant": variantToSimplifiedVariant(variant) }, filterProps, { ref: ref, onKeyUp: composeEventHandlers(onKeyUp, handleKeyUp), className: cn(className, "navds-button", `navds-button--${variant}`, `navds-button--${size}`, {
            "navds-button--loading": loading,
            "navds-button--icon-only": !!icon && !children,
            "navds-button--disabled": disabled !== null && disabled !== void 0 ? disabled : loading,
        }), disabled: (disabled !== null && disabled !== void 0 ? disabled : loading) ? true : undefined }),
        icon && iconPosition === "left" && (React.createElement("span", { className: cn("navds-button__icon") }, icon)),
        loading && React.createElement(Loader, { size: size }),
        children && (React.createElement(Label, { as: "span", size: size === "medium" ? "medium" : "small" }, children)),
        icon && iconPosition === "right" && (React.createElement("span", { className: cn("navds-button__icon") }, icon))));
});
function variantToColor(variant) {
    switch (variant) {
        case "primary-neutral":
        case "secondary-neutral":
        case "tertiary-neutral":
            return "neutral";
        case "danger":
            return "danger";
        default:
            return undefined;
    }
}
function variantToSimplifiedVariant(variant) {
    switch (variant) {
        case "primary":
        case "primary-neutral":
        case "danger":
            return "primary";
        case "secondary":
        case "secondary-neutral":
            return "secondary";
        case "tertiary":
        case "tertiary-neutral":
            return "tertiary";
        default:
            return "primary";
    }
}
export default Button;
//# sourceMappingURL=Button.js.map